import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertType, ApplicationScreen, Clinic } from "../../models";
import { Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import {
	useAlertContext,
	useClinicContext,
	useUserContext
} from "../../contexts";
import { BuildUrl, formatCentreName } from "../../helpers";
import { useMsal } from "@azure/msal-react";
import { apiAccessRequest } from "../../constants";
import { ClinicTable, ConfirmUpdateModal } from ".";
import { CheckinStatus } from "../../types";
import { useCreateAuditLog } from "../../hooks";

export const MobileCheckIn = () => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const { clinics } = useClinicContext();
	const { showAlert, clearAlert } = useAlertContext();

	const showAlertRef = useRef(showAlert);
	const clearAlertRef = useRef(clearAlert);
	useEffect(() => {
		showAlertRef.current = showAlert;
	}, [showAlert]);
	useEffect(() => {
		clearAlertRef.current = clearAlert;
	}, [clearAlert]);

	const [isUpdating, setIsUpdating] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [checkInStatuses, setCheckInStatuses] = useState<CheckinStatus[]>([]);
	const [openConfirmModal, setOpenConfirmModal] = useState(false);
	const [updateClinic, setUpdateclinic] = useState<Clinic | undefined>(
		undefined
	);
	const [openMobileCheckin, setOpenMobileCheckin] = useState(false);
	const { createAuditLog } = useCreateAuditLog();

	const getCheckinStatuses = useCallback(async () => {
		try {
			setIsLoading(true);
			const url = BuildUrl(`clinic/walkinstatuses`);

			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});

			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`
			};

			const response = await fetch(url, { method: "GET", headers });

			if (!response.ok) {
				throw new Error(`Network response was not ok`);
			}

			const checkInStatuses = (await response.json()) as CheckinStatus[];
			const errorMessage = checkInStatuses
				.filter((checkin) => checkin.message !== "")
				.map((checkin) => checkin.message)
				.join(". ");

			if (errorMessage && errorMessage !== "") {
				showAlertRef.current({
					message: errorMessage,
					alertType: AlertType.error,
					timeout: true
				});
			}
			setCheckInStatuses(checkInStatuses);
		} catch (e) {
			console.log(e);
		} finally {
			setIsLoading(false);
		}
	}, [account, instance]);

	useEffect(() => {
		if (!clinics || clinics.length === 0) {
			return;
		}
		getCheckinStatuses();
	}, [clinics, getCheckinStatuses]);

	const handleChange = (event: React.ChangeEvent) => {
		const { id, checked } = event.target as any;
		const clinic = clinics?.find((clinic) => clinic.id.toString() === id);

		if (clinic) {
			setUpdateclinic(clinic);
			setOpenMobileCheckin(checked);
			setOpenConfirmModal(true);
		}
	};

	const callCreateAuditLog = async () => {
		try {
			await createAuditLog([
				{
					clinicId: updateClinic?.id ?? 0,
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminMobileCheckIn,
					action: openMobileCheckin
						? "Enabled mobile check-in"
						: "Disabled mobile check-in"
				}
			]);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	const handleMobileCheckinClose = async (confirm: boolean) => {
		if (!confirm) {
			setUpdateclinic(undefined);
			setOpenMobileCheckin(false);
			setOpenConfirmModal(false);
			return;
		}

		try {
			setIsUpdating(true);
			setIsLoading(false);
			clearAlertRef.current();

			const requestUrl = BuildUrl(
				`Clinic/${updateClinic?.id}/walkinstatus/${openMobileCheckin}`
			);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});

			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};

			const response = await fetch(requestUrl, {
				method: "POST",
				headers
			});

			if (!response.ok) {
				throw new Error("Failed to update setting");
			}

			await getCheckinStatuses();
			showAlertRef.current({
				message: `Mobile check ins for ${formatCentreName(updateClinic?.name ?? "")} are ${openMobileCheckin ? "open" : "closed"}`,
				alertType: AlertType.success,
				timeout: true
			});

			await callCreateAuditLog();
		} catch (error) {
			showAlertRef.current({
				message: `There was an error updating mobile check ins for ${formatCentreName(updateClinic?.name ?? "")}`,
				alertType: AlertType.error
			});
		} finally {
			setUpdateclinic(undefined);
			setOpenMobileCheckin(false);
			setOpenConfirmModal(false);
			setIsUpdating(false);
			window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
		}
	};

	if (isUpdating || isLoading) {
		return (
			<Box className="icon-loading">
				<CircularProgress size={70} />
			</Box>
		);
	}

	return (
		<>
			<ClinicTable
				checkInStatuses={checkInStatuses}
				handleClinicCheckinChange={handleChange}
			/>
			{updateClinic && (
				<ConfirmUpdateModal
					clinic={updateClinic as Clinic}
					openMobileCheckin={openMobileCheckin}
					open={openConfirmModal}
					onClose={handleMobileCheckinClose}
				/>
			)}
		</>
	);
};
